
import { Button } from "@/components/ui/button";
import { Mail, Loader2, UserCheck, CheckCircle } from "lucide-react";
import { useCreatePartnerInvitation } from "@/hooks/use-partner-invitations";
import { useAuth } from "@/contexts/AuthContext";

interface PartnerInviteButtonProps {
  partner: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    user_id?: string;
    invitation_sent_at?: string;
    portal_access_enabled?: boolean;
  };
  size?: "sm" | "default" | "lg";
  variant?: "default" | "outline" | "secondary";
}

export const PartnerInviteButton = ({ 
  partner, 
  size = "sm", 
  variant = "outline" 
}: PartnerInviteButtonProps) => {
  const { session } = useAuth();
  const createInvitation = useCreatePartnerInvitation();

  const handleSendInvitation = () => {
    if (!session?.user?.id) return;
    
    createInvitation.mutate({
      partnerId: partner.id,
      staffId: session.user.id
    });
  };

  // Show different states based on partner status
  if (partner.user_id) {
    // Partner was created from existing user - no invitation needed
    return (
      <Button size={size} variant="secondary" disabled className="w-full bg-green-50 text-green-700 border-green-200">
        <UserCheck className="h-4 w-4 mr-2" />
        Account Active
      </Button>
    );
  }

  if (partner.invitation_sent_at && !partner.user_id) {
    return (
      <Button 
        size={size} 
        variant="outline" 
        onClick={handleSendInvitation}
        disabled={createInvitation.isPending}
        className="w-full"
      >
        {createInvitation.isPending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Mail className="h-4 w-4 mr-2" />
        )}
        Resend Invite
      </Button>
    );
  }

  return (
    <Button 
      size={size} 
      variant={variant} 
      onClick={handleSendInvitation}
      disabled={createInvitation.isPending}
      className="w-full"
    >
      {createInvitation.isPending ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Mail className="h-4 w-4 mr-2" />
      )}
      Send Invite
    </Button>
  );
};
